<script setup lang="ts">
// PrimeVue's own InputMask drives per-character placement off the legacy
// `keypress` event, which real mobile keyboards/IMEs (predictive-text taps,
// autocomplete inserts, some Android keyboard apps) don't reliably fire —
// it then falls back to a whole-string reconciliation pass that scrambles
// digits once mask literals are already present (GT1-306). IMask drives off
// `input`/`beforeinput` and explicitly delays cursor restoration to survive
// mobile browser quirks, so it's used here as the masking engine while
// PrimeVue's InputText remains the (Circuit-styled) visual shell.
import InputText from 'primevue/inputtext';
import IMask, { type InputMask } from 'imask';
import { onBeforeUnmount, onMounted, ref, watch, type ComponentPublicInstance } from 'vue';

const props = defineProps<{
  modelValue: string | null | undefined;
  invalid?: boolean;
  id?: string;
  fluid?: boolean;
  size?: 'small' | 'large';
  dataTestid?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
}>();

function toDigits(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

const inputRef = ref<ComponentPublicInstance | null>(null);
// Single source of truth for InputText's rendered value — IMask owns the
// underlying <input> DOM node directly, and every change it accepts is
// mirrored back here so Vue's own patch cycle never reverts to a stale,
// unmasked value.
const masked = ref('');
let mask: InputMask<{ mask: string }> | null = null;

onMounted(() => {
  const el = inputRef.value?.$el as HTMLInputElement | undefined;
  if (!el) return;
  mask = IMask(el, { mask: '(000) 000-0000' });
  mask.unmaskedValue = toDigits(props.modelValue);
  masked.value = mask.value;
  mask.on('accept', () => {
    if (!mask) return;
    masked.value = mask.value;
    emit('update:modelValue', mask.unmaskedValue || null);
  });
});

onBeforeUnmount(() => {
  mask?.destroy();
  mask = null;
});

watch(() => props.modelValue, (v) => {
  if (!mask) return;
  const incoming = toDigits(v);
  if (incoming !== mask.unmaskedValue) {
    mask.unmaskedValue = incoming;
    masked.value = mask.value;
  }
});
</script>

<template>
  <InputText
    :id="id"
    ref="inputRef"
    :model-value="masked"
    type="tel"
    inputmode="tel"
    placeholder="(555) 123-4567"
    :invalid="invalid"
    :fluid="fluid"
    :size="size"
    :data-testid="dataTestid"
  />
</template>
